import React from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

export type ConfirmDialogProps = {
    isOpen: boolean;
    title: string;
    /**
     * `React.ReactNode`, as the ISR `ConfirmationDialog` typed it — the side panel's
     * "Disable Compatio Applications?" copy is two sentences split by a `<br />`.
     */
    message: React.ReactNode;
    secondaryMessage?: string;
    confirmText?: string;
    cancelText?: string;
    /**
     * `danger` paints the confirm red and puts the tone glyph beside the title (§ 53).
     * Omit it for a confirm that is not destructive — changing which environment is
     * production is a decision, not a deletion, and a red button there teaches people
     * to ignore red.
     */
    tone?: 'danger';
    /** Label shown on the confirm button while the request is in flight. */
    busyText?: string;
    isLoading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    'data-testid': string;
};

/**
 * The Party-management confirm dialog (§ 120).
 *
 * This module had **four** of these — Delete Party, Delete Domain, Delete Channel,
 * Delete Environment — plus a fifth non-destructive one (Change Production
 * Environment), each previously the ISR `ConfirmationDialog`: a plain `<div>` with no
 * `role="dialog"`, no `aria-modal`, no focus trap, no focus restore and no Escape.
 * Migrating them one at a time meant writing the same twenty lines of `Dialog` +
 * footer five times, and `ChannelTable` alone needs three.
 *
 * So the shape is extracted here rather than repeated. Two behaviours belong to this
 * component and are easy to get wrong when hand-rolled:
 *
 * - **Both actions are refused while loading**, as `ConfirmationDialog` did — and
 *   `Dialog` adds Escape as a THIRD route to `onCancel`, so the guard has to live in
 *   the handler rather than on the buttons.
 * - **The confirm's busy label** replaces its text mid-request ("Processing…"), which
 *   is the only feedback the old dialog gave and the only one a spec can assert.
 *
 * The testid contract is `ConfirmationDialog`'s, so no spec had to change:
 * `{testid}`, `{testid}-message`, `{testid}-secondary-message`, `{testid}-btn` for
 * confirm and `{testid}-cancel-btn`.
 */
const ConfirmDialog = ({
    isOpen,
    title,
    message,
    secondaryMessage,
    confirmText = 'Delete',
    cancelText = 'Cancel',
    tone,
    busyText = 'Processing...',
    isLoading = false,
    onConfirm,
    onCancel,
    'data-testid': dataTestId,
}: ConfirmDialogProps) => {
    const guardedCancel = () => {
        if (!isLoading) {
            onCancel();
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={guardedCancel}
            title={title}
            tone={tone}
            size="sm"
            data-testid={dataTestId}
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <Button
                        variant="secondary"
                        id={`${dataTestId}-cancel-btn`}
                        data-testid={`${dataTestId}-cancel-btn`}
                        onClick={guardedCancel}
                        disabled={isLoading}
                    >
                        {cancelText}
                    </Button>
                    <Button
                        /* § 167: the FILLED destructive confirm, not the outlined
                           `danger` action. All four `danger` uses here are deletes
                           (Delete Party / Domain / Channel / Environment); the fifth,
                           Change Production, is not `danger` and stays `primary`. */
                        variant={tone === 'danger' ? 'danger-primary' : 'primary'}
                        id={`${dataTestId}-btn`}
                        data-testid={`${dataTestId}-btn`}
                        onClick={() => { if (!isLoading) { onConfirm(); } }}
                        disabled={isLoading}
                    >
                        {isLoading ? busyText : confirmText}
                    </Button>
                </div>
            }
        >
            <div className="tw-space-y-2">
                <Typography variant="title" as="p" data-testid={`${dataTestId}-message`}>
                    {message}
                </Typography>
                {secondaryMessage ? (
                    <Typography variant="title" as="p" data-testid={`${dataTestId}-secondary-message`}>
                        {secondaryMessage}
                    </Typography>
                ) : null}
            </div>
        </Dialog>
    );
};

export default ConfirmDialog;
